import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, 
  Save, 
  Eye, 
  EyeOff, 
  ExternalLink, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Loader2,
  Mail,
  HardDrive,
  Calendar,
  Video,
  MessageSquare,
  Send,
  Hash,
  FileSpreadsheet,
  Github,
  Cloud,
  FileSignature,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Integration configurations
const INTEGRATIONS = [
  {
    id: 'gmail',
    name: 'Gmail',
    icon: Mail,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    description: 'Send and receive emails directly from the CRM',
    provider: 'google',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['gmail.readonly', 'gmail.send', 'gmail.modify']
  },
  {
    id: 'google_drive',
    name: 'Google Drive',
    icon: HardDrive,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    description: 'Store candidate documents with auto-organization',
    provider: 'google',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['drive.file', 'drive.metadata.readonly']
  },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    icon: Calendar,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    description: 'Schedule interviews and send calendar invites',
    provider: 'google',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['calendar', 'calendar.events']
  },
  {
    id: 'google_meet',
    name: 'Google Meet',
    icon: Video,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    description: 'Auto-create meeting links for interviews',
    provider: 'google',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['calendar']
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: Hash,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    description: 'Send notifications and updates to Slack channels',
    provider: 'slack',
    setupUrl: 'https://api.slack.com/apps',
    requiredFields: ['client_id', 'client_secret'],
    optionalFields: ['bot_token', 'signing_secret'],
    scopes: ['channels:read', 'chat:write', 'users:read']
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: MessageSquare,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    description: 'Send updates to Discord channels via webhooks',
    provider: 'discord',
    setupUrl: 'https://discord.com/developers/applications',
    requiredFields: ['client_id', 'client_secret'],
    optionalFields: ['webhook_url'],
    scopes: ['identify', 'guilds']
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: Send,
    color: 'text-sky-500',
    bgColor: 'bg-sky-50',
    description: 'Send notifications via Telegram bot',
    provider: 'telegram',
    setupUrl: 'https://t.me/BotFather',
    requiredFields: ['bot_token'],
    scopes: ['messages', 'notifications']
  },
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    icon: FileSpreadsheet,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    description: 'Export data and reports to spreadsheets',
    provider: 'google',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['spreadsheets']
  },
  {
    id: 'github',
    name: 'GitHub',
    icon: Github,
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    description: 'View candidate repositories and contributions',
    provider: 'github',
    setupUrl: 'https://github.com/settings/developers',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['read:user', 'repo']
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    icon: Cloud,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    description: 'Alternative file storage for documents',
    provider: 'dropbox',
    setupUrl: 'https://www.dropbox.com/developers/apps',
    requiredFields: ['client_id', 'client_secret'],
    scopes: ['files.content.read', 'files.content.write']
  },
  {
    id: 'rabbitsign',
    name: 'RabbitSign',
    icon: FileSignature,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    description: 'E-signatures for offer letters and contracts',
    provider: 'rabbitsign',
    setupUrl: 'https://rabbitsign.com/developers',
    requiredFields: ['api_key'],
    scopes: ['signatures', 'documents']
  }
];

const IntegrationSettingsPanel = () => {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [formData, setFormData] = useState({});
  const [activeCategory, setActiveCategory] = useState('google');

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/integrations/configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert array to object keyed by app_id
      const configsObj = {};
      (response.data || []).forEach(config => {
        configsObj[config.app_id] = config;
      });
      setConfigs(configsObj);
      
      // Initialize form data
      const initialFormData = {};
      INTEGRATIONS.forEach(integration => {
        const existingConfig = configsObj[integration.id] || {};
        initialFormData[integration.id] = {
          client_id: existingConfig.client_id || '',
          client_secret: existingConfig.client_secret || '',
          api_key: existingConfig.api_key || '',
          bot_token: existingConfig.bot_token || '',
          webhook_url: existingConfig.webhook_url || '',
          signing_secret: existingConfig.signing_secret || ''
        };
      });
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Failed to load integration configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (integrationId) => {
    setSaving(prev => ({ ...prev, [integrationId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const data = formData[integrationId];
      
      // Only send non-empty fields
      const configData = { app_id: integrationId };
      if (data.client_id) configData.client_id = data.client_id;
      if (data.client_secret && !data.client_secret.startsWith('****')) {
        configData.client_secret = data.client_secret;
      }
      if (data.api_key && !data.api_key.startsWith('****')) {
        configData.api_key = data.api_key;
      }
      if (data.bot_token && !data.bot_token.startsWith('****')) {
        configData.bot_token = data.bot_token;
      }
      if (data.webhook_url) configData.webhook_url = data.webhook_url;
      if (data.signing_secret && !data.signing_secret.startsWith('****')) {
        configData.signing_secret = data.signing_secret;
      }
      
      await axios.post(`${API}/api/admin/integrations/configure`, configData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${INTEGRATIONS.find(i => i.id === integrationId)?.name} configuration saved!`);
      
      // Refresh configs
      fetchConfigs();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(prev => ({ ...prev, [integrationId]: false }));
    }
  };

  const handleInputChange = (integrationId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [integrationId]: {
        ...prev[integrationId],
        [field]: value
      }
    }));
  };

  const toggleShowSecret = (integrationId, field) => {
    const key = `${integrationId}_${field}`;
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isConfigured = (integrationId) => {
    const config = configs[integrationId];
    const integration = INTEGRATIONS.find(i => i.id === integrationId);
    if (!config || !integration) return false;
    
    return integration.requiredFields.every(field => {
      const value = config[field];
      return value && value.length > 0;
    });
  };

  const getCategories = () => {
    const categories = {
      google: { name: 'Google Services', integrations: [] },
      communication: { name: 'Communication', integrations: [] },
      storage: { name: 'Storage', integrations: [] },
      other: { name: 'Other', integrations: [] }
    };
    
    INTEGRATIONS.forEach(integration => {
      if (integration.provider === 'google') {
        categories.google.integrations.push(integration);
      } else if (['slack', 'discord', 'telegram'].includes(integration.id)) {
        categories.communication.integrations.push(integration);
      } else if (['dropbox', 'onedrive'].includes(integration.id)) {
        categories.storage.integrations.push(integration);
      } else {
        categories.other.integrations.push(integration);
      }
    });
    
    return categories;
  };

  const categories = getCategories();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const renderIntegrationCard = (integration) => {
    const Icon = integration.icon;
    const configured = isConfigured(integration.id);
    const data = formData[integration.id] || {};
    const isSaving = saving[integration.id];

    return (
      <Card key={integration.id} className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${integration.bgColor}`}>
                <Icon className={`h-5 w-5 ${integration.color}`} />
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  {integration.name}
                  {configured ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                      <AlertTriangle className="h-3 w-3 mr-1" /> Not Configured
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-sm">{integration.description}</CardDescription>
              </div>
            </div>
            <a 
              href={integration.setupUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"
            >
              Setup Guide <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Client ID */}
          {integration.requiredFields.includes('client_id') && (
            <div className="space-y-1.5">
              <Label htmlFor={`${integration.id}-client-id`} className="text-sm">
                Client ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id={`${integration.id}-client-id`}
                value={data.client_id || ''}
                onChange={(e) => handleInputChange(integration.id, 'client_id', e.target.value)}
                placeholder="Enter Client ID"
                className="font-mono text-sm"
              />
            </div>
          )}

          {/* Client Secret */}
          {integration.requiredFields.includes('client_secret') && (
            <div className="space-y-1.5">
              <Label htmlFor={`${integration.id}-client-secret`} className="text-sm">
                Client Secret <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id={`${integration.id}-client-secret`}
                  type={showSecrets[`${integration.id}_client_secret`] ? 'text' : 'password'}
                  value={data.client_secret || ''}
                  onChange={(e) => handleInputChange(integration.id, 'client_secret', e.target.value)}
                  placeholder="Enter Client Secret"
                  className="font-mono text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret(integration.id, 'client_secret')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecrets[`${integration.id}_client_secret`] ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* API Key (for services like RabbitSign) */}
          {integration.requiredFields.includes('api_key') && (
            <div className="space-y-1.5">
              <Label htmlFor={`${integration.id}-api-key`} className="text-sm">
                API Key <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id={`${integration.id}-api-key`}
                  type={showSecrets[`${integration.id}_api_key`] ? 'text' : 'password'}
                  value={data.api_key || ''}
                  onChange={(e) => handleInputChange(integration.id, 'api_key', e.target.value)}
                  placeholder="Enter API Key"
                  className="font-mono text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret(integration.id, 'api_key')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecrets[`${integration.id}_api_key`] ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Bot Token (for Telegram/Slack) */}
          {integration.requiredFields.includes('bot_token') && (
            <div className="space-y-1.5">
              <Label htmlFor={`${integration.id}-bot-token`} className="text-sm">
                Bot Token <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id={`${integration.id}-bot-token`}
                  type={showSecrets[`${integration.id}_bot_token`] ? 'text' : 'password'}
                  value={data.bot_token || ''}
                  onChange={(e) => handleInputChange(integration.id, 'bot_token', e.target.value)}
                  placeholder="Enter Bot Token"
                  className="font-mono text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret(integration.id, 'bot_token')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecrets[`${integration.id}_bot_token`] ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Optional: Webhook URL */}
          {integration.optionalFields?.includes('webhook_url') && (
            <div className="space-y-1.5">
              <Label htmlFor={`${integration.id}-webhook-url`} className="text-sm">
                Webhook URL <span className="text-slate-400">(optional)</span>
              </Label>
              <Input
                id={`${integration.id}-webhook-url`}
                value={data.webhook_url || ''}
                onChange={(e) => handleInputChange(integration.id, 'webhook_url', e.target.value)}
                placeholder="https://..."
                className="font-mono text-sm"
              />
            </div>
          )}

          {/* Redirect URI Info */}
          {integration.provider === 'google' && (
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-medium mb-1">Redirect URIs to add in Google Console:</p>
              <code className="block bg-white px-2 py-1 rounded border text-xs break-all">
                {window.location.origin}/auth/google/callback
              </code>
            </div>
          )}
          
          {integration.id === 'slack' && (
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-medium mb-1">Redirect URI for Slack App:</p>
              <code className="block bg-white px-2 py-1 rounded border text-xs break-all">
                {window.location.origin}/auth/slack/callback
              </code>
            </div>
          )}
          
          {integration.id === 'discord' && (
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-medium mb-1">Redirect URI for Discord App:</p>
              <code className="block bg-white px-2 py-1 rounded border text-xs break-all">
                {window.location.origin}/auth/discord/callback
              </code>
            </div>
          )}

          <Button 
            onClick={() => handleSave(integration.id)}
            disabled={isSaving}
            className="w-full bg-violet-600 hover:bg-violet-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" /> Save Configuration
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="integration-settings-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Settings className="h-5 w-5 text-violet-500" />
            Integration Settings
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Configure OAuth credentials for third-party integrations
          </p>
        </div>
        <Button variant="outline" onClick={fetchConfigs} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {INTEGRATIONS.filter(i => isConfigured(i.id)).length}
                </p>
                <p className="text-xs text-green-600">Configured</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-yellow-700">
                  {INTEGRATIONS.filter(i => !isConfigured(i.id)).length}
                </p>
                <p className="text-xs text-yellow-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Mail className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {categories.google.integrations.length}
                </p>
                <p className="text-xs text-blue-600">Google Services</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {categories.communication.integrations.length}
                </p>
                <p className="text-xs text-purple-600">Communication</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="grid w-full grid-cols-4 bg-slate-100">
          <TabsTrigger value="google" className="data-[state=active]:bg-white">
            Google Services
          </TabsTrigger>
          <TabsTrigger value="communication" className="data-[state=active]:bg-white">
            Communication
          </TabsTrigger>
          <TabsTrigger value="storage" className="data-[state=active]:bg-white">
            Storage
          </TabsTrigger>
          <TabsTrigger value="other" className="data-[state=active]:bg-white">
            Other
          </TabsTrigger>
        </TabsList>

        {Object.entries(categories).map(([key, category]) => (
          <TabsContent key={key} value={key} className="mt-4">
            {category.integrations.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {category.integrations.map(renderIntegrationCard)}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center text-slate-500">
                  <XCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p>No integrations in this category</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Help Section */}
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-100">
        <CardContent className="p-4">
          <h4 className="font-medium text-violet-800 mb-2">Need Help?</h4>
          <ul className="text-sm text-violet-700 space-y-1">
            <li>• <strong>Google Services:</strong> Create a project in Google Cloud Console, enable required APIs, and create OAuth 2.0 credentials</li>
            <li>• <strong>Slack:</strong> Create an app at api.slack.com, add OAuth scopes, and install to your workspace</li>
            <li>• <strong>Discord:</strong> Create an application at discord.com/developers and configure OAuth2</li>
            <li>• <strong>Telegram:</strong> Create a bot via @BotFather and copy the bot token</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationSettingsPanel;
